import React, { useState } from 'react';
import { Bell, X } from 'lucide-react';
import { useNotifications, Notification, UseNotificationsResult } from './useNotifications';
import { requestNotificationPermission, canRequestNotificationPermission } from '@clareefai/utils';

export interface NotificationBellProps {
  /** Firestore collection path: "vendors" | "promoters" | "prospects" | "admins" */
  collectionPath: string;
  /** Firebase UID of the current user */
  firebaseUid: string | null;
  /** App name for document title */
  appName: string;
  /** Visual variant */
  variant?: 'default' | 'light' | 'dark';
  /** Called when user clicks a notification — use to navigate based on type */
  onNotificationClick?: (notification: Notification) => void;
  /** Optional custom render for each notification row */
  renderNotification?: (notification: Notification, defaultContent: React.ReactNode) => React.ReactNode;
  /** Called when new notifications arrive (not fired for historical or read-updates) */
  onNewNotifications?: (newNotifications: Notification[]) => void;
  /**
   * When the host already owns a `useNotifications` subscription, pass its
   * result here to avoid opening a second Firestore listener (and firing
   * sounds/browser notifications twice).
   */
  source?: UseNotificationsResult;
}

function formatTime(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  return date.toLocaleDateString();
}

export const NotificationBell: React.FC<NotificationBellProps> = ({
  collectionPath,
  firebaseUid,
  appName,
  variant = 'default',
  onNotificationClick,
  renderNotification,
  onNewNotifications,
  source,
}) => {
  const internal = useNotifications({
    collectionPath,
    firebaseUid,
    appName,
    onNewNotifications,
    enabled: !source,
  });
  const { notifications, unreadCount, markAsRead, markAllAsRead } = source ?? internal;
  const [isOpen, setIsOpen] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission | null>(null);

  const handleBellClick = () => {
    const next = !isOpen;
    setIsOpen(next);
    if (next) {
      if (typeof window !== 'undefined' && 'Notification' in window) setPermission(window.Notification.permission);
      if (canRequestNotificationPermission()) requestNotificationPermission().then(setPermission);
    }
  };

  const currentPermission = permission ?? (typeof window !== 'undefined' && 'Notification' in window ? window.Notification.permission : 'denied');
  const showEnableBanner = currentPermission === 'default';

  const buttonClass =
    variant === 'dark'
      ? 'relative p-2 rounded-full transition-colors text-white hover:text-gray-200 hover:bg-indigo-700'
      : variant === 'light'
        ? 'relative p-2 hover:bg-white/10 rounded-lg transition-colors duration-200'
        : 'relative p-2 text-gray-600 hover:text-gray-900 transition-colors';
  const iconClass = variant === 'light' ? 'h-6 w-6 text-white' : 'h-6 w-6';

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) markAsRead(notification.id);
    onNotificationClick?.(notification);
    setIsOpen(false);
  };

  const defaultNotificationContent = (notification: Notification) => (
    <>
      <p className={`text-sm font-medium ${notification.read ? 'text-gray-700' : 'text-gray-900'}`}>
        {notification.title}
      </p>
      <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
      <p className="text-xs text-gray-400 mt-2">{formatTime(notification.createdAt)}</p>
    </>
  );

  if (!firebaseUid) return null;

  return (
    <div className="relative">
      <button onClick={handleBellClick} className={buttonClass}>
        <Bell className={iconClass} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl z-20 border border-gray-200 max-h-[600px] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">
                Notifications{unreadCount > 0 ? ` (${unreadCount})` : ''}
              </h3>
              {unreadCount > 0 && (
                <button onClick={markAllAsRead} className="text-sm text-blue-600 hover:text-blue-800">
                  Mark all as read
                </button>
              )}
              <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            {showEnableBanner && (
              <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
                <p className="text-xs text-blue-800 mb-1">Get alerts when the tab is in background</p>
                <button
                  onClick={() => requestNotificationPermission().then((p) => setPermission(p))}
                  className="text-xs font-medium text-blue-600 hover:text-blue-800"
                >
                  Enable browser notifications
                </button>
              </div>
            )}

            <div className="overflow-y-auto flex-1">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Bell className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No notifications</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {notifications.map((notification) => {
                    const content = renderNotification
                      ? renderNotification(notification, defaultNotificationContent(notification))
                      : defaultNotificationContent(notification);
                    return (
                      <div
                        key={notification.id}
                        className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer border-l-4 ${
                          notification.read ? 'bg-gray-50 border-gray-200' : 'bg-blue-50 border-blue-200'
                        }`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-1 min-w-0">{content}</div>
                          {!notification.read && (
                            <div className="flex-shrink-0">
                              <div className="h-2 w-2 bg-blue-500 rounded-full" />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
